import React, { useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import ResponsiveDrawer from "../../component/sidebar/sidebar.component";
import {
  Alert,
  Box,
  Chip,
  CircularProgress,
  Grid,
  Paper,
  Stack,
  Typography,
} from "@mui/material";
import { FontFamily } from "../../component/theme/fontFamily";
import { fontSize } from "../../component/theme/fontSize";
import { fontWeight } from "../../component/theme/fontWeight";
import GlobalSearch from "../../component/sidebar/globalSearch";
import { buildSearchSections } from "./searchUtils";
import { getGlobalSearch } from "../../Redux/service/DashboardService";

export const getItemId = (item) =>
  item?._id || item?.id || item?.applicationId || item?.studentId || item?.organizationId;

export const buildResultRoute = (sectionKey, item) => {
  const id = getItemId(item);
  const routes = {
    caeUsers: id ? `/CAE/EditUser/${id}` : "/CAE/UserList",
    organizations: id ? `/CAE/EditOrganization/${id}` : "/CAE/OrganizationList",
    universities: id ? `/CAE/University-overview/${id}` : "/CAE/Universitylist",
    programs: id ? `/CAE/ProgramList/${id}` : "/CAE/ProgramList",
    brochures: id ? `/CAE/EditCatalogandBrochure/${id}` : "/CAE/CatalogueBrochureList",
    processSheets: id ? `/CAE/ProcessList/${id}` : "/CAE/ProcessList",
    applicants: id ? `/CAE/EditApplication/${id}` : "/CAE/Applicationlist",
    visaAccounts: "/CAE/Applicationlist",
    chats: "/CAE/chatlist",
    studentChats: "/CAE/chatlist",
    studentNotes: "/CAE/SettingDashboard",
    degrees: "/CAE/DegreeList",
    countryStates: "/CAE/StateList",
    contractTypes: id ? `/CAE/EditContract/${id}` : "/CAE/ContractList",
    folders: id ? `/CAE/FileManager/Folder/${id}` : "/CAE/Filemanager",
    files: "/CAE/Filemanager",
    notifications: "/CAE/AllNotification",
    default: "/CAE/SettingDashboard",
  };

  return routes[sectionKey] || routes.default;
};

export const handleSearchResultClick = (sectionKey, item, navigate) => {
  if (sectionKey === "files" && item?.url) {
    window.open(item.url, "_blank", "noopener,noreferrer");
    return;
  }

  const route = buildResultRoute(sectionKey, item);
  navigate(route, { replace: true, state: { searchItem: item } });
};

export const GlobalSearchPageInner = () => {
  const [searchResults, setSearchResults] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [hasSearched, setHasSearched] = useState(false);
  const navigate = useNavigate();

  const handleResultClick = (sectionKey, item) => {
    handleSearchResultClick(sectionKey, item, navigate);
  };

  const handleSearch = async (value) => {
    const trimmedValue = value?.trim();

    if (!trimmedValue) {
      setSearchResults([]);
      setHasSearched(false);
      setErrorMessage("");
      return;
    }

    setIsLoading(true);
    setHasSearched(true);
    setErrorMessage("");

    try {
      const token =
        window.sessionStorage.getItem("user_token") ||
        window.localStorage.getItem("user_token");

      const response = await getGlobalSearch(trimmedValue);
      if (response?.data) {
        setSearchResults(buildSearchSections(response.data));
      } else {
        setSearchResults([]);
        setErrorMessage(response?.data?.message || "No results found.");
      }
    } catch (err) {
      console.error(err);
      setSearchResults([]);
      setErrorMessage(
        err?.response?.data?.message || "Search failed. Please try again."
      );
    } finally {
      setIsLoading(false);
    }
  };

  const renderItem = (sectionKey, item) => {
    switch (sectionKey) {
      case "caeUsers":
        return (
          <Box>
            <Typography fontWeight={fontWeight.W600}>
              {item.firstName} {item.lastName}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {item.email}
            </Typography> 
            <Typography variant="body2">Role: {item.roleType}</Typography>
          </Box>
        );
      case "organizations":
        return (
          <Box>
            <Typography fontWeight={fontWeight.W600}>
              {item.organizationName}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {item.country} • {item.city}
            </Typography>
          </Box>
        );
      case "universities":
        return (
          <Box>
            <Typography fontWeight={fontWeight.W600}>{item.name}</Typography>
            <Typography variant="body2" color="text.secondary">
              {item.country} • {item.city}
            </Typography>
          </Box>
        );
      case "programs":
        return (
          <Box>
            <Typography fontWeight={fontWeight.W600}>{item.program}</Typography>
            <Typography variant="body2" color="text.secondary">
              {item.degree?.name} • {item.university?.name}
            </Typography>
          </Box>
        );
      case "brochures":
        return (
          <Box>
            <Typography fontWeight={fontWeight.W600}>
              {item.university?.name || "Brochure"}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {item.fileIds?.length || 0} file(s)
            </Typography>
          </Box>
        );
      case "applicants":
      case "visaAccounts":
        return (
          <Box>
            <Typography fontWeight={fontWeight.W600}>
              {item.studentFirstName} {item.studentLastName}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {item.studentEmailId}
            </Typography>
            <Typography variant="body2">Reference: {item.referenceId}</Typography>
          </Box>
        );
      case "countryStates":
        return (
          <Box>
            <Typography fontWeight={fontWeight.W600}>{item.country}</Typography>
            <Typography variant="body2" color="text.secondary">
              {item.states?.join(", ")}
            </Typography>
          </Box>
        );
      case "contractTypes":
        return (
          <Box>
            <Typography fontWeight={fontWeight.W600}>{item.contractType}</Typography>
          </Box>
        );
      case "files":
        return (
          <Box>
            <Typography fontWeight={fontWeight.W600}>{item.name}</Typography>
            {item.url ? (
              <Typography variant="body2" color="text.secondary">
                {item.url}
              </Typography>
            ) : null}
          </Box>
        );
      case "notifications":
        return (
          <Box>
            <Typography fontWeight={fontWeight.W600}>{item.title}</Typography>
            <Typography variant="body2" color="text.secondary">
              {item.text}
            </Typography>
          </Box>
        );

      case "chats":
        return (
          <Box>
            <Typography fontWeight={fontWeight.W600}>{item.groupName}</Typography>
            <Typography variant="body2" color="text.secondary">
              {"Total people "+item.participants.length}
            </Typography>
          </Box>
        );
      default:
        return (
          <Box>
            <Typography fontWeight={fontWeight.W600}>{item.name || item.title || item._id}</Typography>
            {item.description ? (
              <Typography variant="body2" color="text.secondary">
                {item.description}
              </Typography>
            ) : null}
          </Box>
        );
    }
  };

  return (
    <Box sx={{ p: 4, width: "100%" }}>
      <Grid container justifyContent="center">
        <Grid item xs={12} md={10} lg={8}>
          <Box sx={{ textAlign: "center", mb: 4 }}>
            <Typography
              fontFamily={FontFamily.Poppins}
              fontSize={fontSize.h2.small}
              fontWeight={fontWeight.W600}
            >
              Global Search
            </Typography>
            <Typography color="text.secondary" sx={{ mt: 1 }}>
              Search across students, organizations, files and more
            </Typography>
          </Box>

          <Box sx={{ bgcolor: "transparent" }}>
            <GlobalSearch onSearch={handleSearch} />
          </Box>

          {isLoading && (
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mt: 3 }}>
              <CircularProgress size={20} />
              <Typography color="text.secondary">Searching...</Typography>
            </Box>
          )}

          {errorMessage && (
            <Alert severity="error" sx={{ mt: 3 }}>
              {errorMessage}
            </Alert>
          )}

          {!isLoading && !errorMessage && hasSearched && searchResults.length === 0 && (
            <Alert severity="info" sx={{ mt: 3 }}>
              No results found for this search.
            </Alert>
          )}

          <Stack spacing={2} sx={{ mt: 3 }}>
            {searchResults.map((section) => (
              <Paper key={section.key} variant="outlined" sx={{ p: 2 }}>
                <Box sx={{ display: "flex", alignItems: "center", mb: 1.5 }}>
                  <Typography fontWeight={fontWeight.W700} sx={{ flexGrow: 1 }}>
                    {section.label}
                  </Typography>
                  <Chip label={`${section.items.length} result(s)`} size="small" />
                </Box>

                <Stack spacing={1.25}>
                  {section.items.map((item, index) => (
                    <Paper
                      key={item._id || `${section.key}-${index}`}
                      variant="outlined"
                      onClick={() => handleResultClick(section.key, item)}
                      sx={{
                        p: 1.5,
                        bgcolor: "background.paper",
                        cursor: "pointer",
                        transition: "background-color 150ms ease",
                        '&:hover': { bgcolor: "action.hover" },
                      }}
                    >
                      {renderItem(section.key, item)}
                    </Paper>
                  ))}
                </Stack>
              </Paper>
            ))}
          </Stack>
        </Grid>
      </Grid>
    </Box>
  );
};

const GlobalSearchPage = () => {
  return (
    <div>
      <ResponsiveDrawer maincontent={<GlobalSearchPageInner />} />
    </div>
  );
};

export default GlobalSearchPage;
